import { useMemo } from 'react';

import { useNow } from '@change-corgi/core-react/date';
import { useI18n } from '@change-corgi/core-react/i18n';

import { usePetitionDetailsPageData } from '#src/app/pages/petitionDetails/pageContext';
import { useEndorsements } from '#src/app/shared/components/endorsements';
import { useSearchTopicFacetValues } from '#src/app/shared/hooks/useSearchTopicFacetValues';

import type { ResolverContext } from './heroResolvers';
import { heroResolvers } from './heroResolvers';
import type { HeroActivityType, HeroResult, SpotlightContent } from './types';
import {
	ACTIVITY_TYPES,
	getDaysAgo,
	NEW_PETITION_DAYS_THRESHOLD,
	sortCandidates,
	UNDATED_ACTIVITY_PRIORITY,
	getSpotlightCategoryFromSearchTopicFacets,
} from './utils';

const EMPTY_TOPIC_FACET_VALUES: readonly string[] = [];

/**
 * Returns all available spotlight items sorted by recency, falling back to a
 * single static item when no activity is present. The first element is always
 * the most prominent content to display.
 */
export function useSpotlightCardContent(): SpotlightContent[] {
	const now = useNow();
	const { translate } = useI18n();
	const { data } = usePetitionDetailsPageData();
	const searchTopicFacetsQuery = useSearchTopicFacetValues();
	const {
		data: { endorsements },
	} = useEndorsements({ petitionSlugOrId: data.petition.id, first: 1 });
	const latestEndorsement = endorsements[0] ?? null;

	// Use search facet values only when loaded; otherwise keep a stable empty allowlist
	// so we fall back to the default spotlight category without forcing memo recomputation.
	const searchTopicFacetValues =
		searchTopicFacetsQuery.status === 'loaded' ? searchTopicFacetsQuery.data : EMPTY_TOPIC_FACET_VALUES;

	return useMemo(() => {
		const ctx: ResolverContext = { data, translate, latestEndorsement };

		const candidates: Array<[HeroActivityType, HeroResult]> = [];
		for (const activityType of ACTIVITY_TYPES) {
			const result = heroResolvers[activityType](ctx);
			if (result) candidates.push([activityType, result]);
		}

		const sorted = sortCandidates(candidates, UNDATED_ACTIVITY_PRIORITY);
		if (sorted.length > 0) return sorted;

		const { createdAt, tagsConnection } = data.petition;
		const daysFromCreation = getDaysAgo(now, createdAt) ?? 0;

		const category =
			getSpotlightCategoryFromSearchTopicFacets(tagsConnection, searchTopicFacetValues) ??
			translate('corgi.petition_details.momentum_bar.spotlight.default_category');
		const isNewPetition = daysFromCreation <= NEW_PETITION_DAYS_THRESHOLD;

		return [
			{
				badgeLabel: isNewPetition
					? translate('corgi.petition_details.momentum_bar.spotlight.new_petition_badge')
					: translate('corgi.petition_details.momentum_bar.spotlight.petition_progress_badge'),
				text: translate('corgi.petition_details.momentum_bar.spotlight.gaining_momentum_text', { category }),
				showIcon: true,
			},
		];
	}, [now, translate, data, latestEndorsement, searchTopicFacetValues]);
}
